// Per-hex actor indices, in the per-hex block of the political atlas (atlas.ts):
//
//   channels  r / g                      b / a
//   ───────   ────────────────────────   ─────────────────────────────
//   claims    default owner of a hex     city-state whose capital is
//             the leader's view assigns  this hex (city-state ring)
//             to another state (low /
//             high byte of the index)
//
// Claims come from snapshot.claim: the border shader hatches those hexes and dashes their outline in
// the default owner's colour. Zero means none. Uploads happen only when a hex changed.

import { ivec2, textureLoad } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { COLS, HEX_COUNT } from '../../core/grid.ts';
import { cellCoord, type HexCell } from '../tsl/hex.ts';
import { ATLAS_W, CHANNELS, HEX_BLOCK_Y, type Atlas } from './atlas.ts';

const BYTE = 255;
const HIGH_SHIFT = 8;

/** Offset of a field's low byte inside a texel; the high byte follows it. */
export const CLAIM_CHANNEL = 0;
export const RING_CHANNEL = 2;
export type FieldChannel = typeof CLAIM_CHANNEL | typeof RING_CHANNEL;

export interface ClaimPack {
  /** Hexes whose texel changed. */
  changed: number;
  /** Hexes that carry a claim. */
  claimed: number;
}

/**
 * Write `claim` (actor index per hex, 0 = none) into the atlas bytes at `channel`; only differing
 * texels are written.
 */
export function packClaims(claim: Uint16Array | undefined, out: Uint8Array, channel: FieldChannel = CLAIM_CHANNEL): ClaimPack {
  let changed = 0;
  let claimed = 0;

  for (let i = 0; i < HEX_COUNT; i++) {
    const value = claim ? claim[i] : 0;
    const lo = value & BYTE;
    const hi = value >> HIGH_SHIFT;
    const row = Math.floor(i / COLS);
    const at = ((HEX_BLOCK_Y + row) * ATLAS_W + (i - row * COLS)) * CHANNELS;
    if (value !== 0) {
      claimed++;
    }
    if (out[at + channel] === lo && out[at + channel + 1] === hi) {
      continue;
    }

    out[at + channel] = lo;
    out[at + channel + 1] = hi;
    changed++;
  }

  return { changed, claimed };
}

export class ClaimField {
  private readonly atlas: Atlas;
  private lastClaimed = 0;
  private lastRings = 0;

  constructor(atlas: Atlas) {
    this.atlas = atlas;
  }

  /** Hexes carrying a claim in the last snapshot. */
  claimed(): number {
    return this.lastClaimed;
  }

  /** City-state rings set by the last setRings. */
  rings(): number {
    return this.lastRings;
  }

  /** Upload a snapshot's claims (undefined clears them). */
  set(claim: Uint16Array | undefined): void {
    this.upload(claim, CLAIM_CHANNEL);
  }

  /** Mark each hex holding a city-state capital with the state's actor index (undefined clears). */
  setRings(rings: Uint16Array | undefined): void {
    this.upload(rings, RING_CHANNEL);
  }

  /** Default owner actor index of a cell (0 = the view agrees with the default worldview). */
  at(cell: HexCell): Node<'float'> {
    return this.read(cell, CLAIM_CHANNEL);
  }

  /** Actor index of the city-state whose capital is this cell (0 = none). */
  ringAt(cell: HexCell): Node<'float'> {
    return this.read(cell, RING_CHANNEL);
  }

  private upload(values: Uint16Array | undefined, channel: FieldChannel): void {
    const pack = packClaims(values, this.atlas.data, channel);
    if (channel === CLAIM_CHANNEL) {
      this.lastClaimed = pack.claimed;
    } else {
      this.lastRings = pack.claimed;
    }
    if (pack.changed > 0) {
      this.atlas.touch();
    }
  }

  private read(cell: HexCell, channel: FieldChannel): Node<'float'> {
    const v = textureLoad(this.atlas.texture, cellCoord(cell).add(ivec2(0, HEX_BLOCK_Y))).mul(BYTE).round();
    const low = channel === CLAIM_CHANNEL ? v.x : v.z;
    const high = channel === CLAIM_CHANNEL ? v.y : v.w;
    return low.add(high.mul(1 << HIGH_SHIFT));
  }
}
